'use client';

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface BankDetails {
  bankName: string;
  accountName: string;
  accountNumber: string;
  branchCode: string;
  swiftCode: string;
}

export interface CompanySettings {
  companyName: string;
  companyTagline: string;
  companyEmail: string;
  companyPhone: string;
  companyAddress: string;
  companyVatNumber: string;
  logoUrl: string;
  taxRate: number;
  vatIncluded: boolean;
  currency: string;
  senderEmails: string[];
  bankDetails: BankDetails;
}

interface CompanySettingsContextValue {
  settings: CompanySettings;
  updateSettings: (partial: Partial<CompanySettings>) => void;
  resetSettings: () => void;
}

const DEFAULT_SETTINGS: CompanySettings = {
  companyName: 'Kuttingedge Solutions',
  companyTagline: 'Media & Events · Harare, Zimbabwe',
  companyEmail: 'info@kes.co.zw',
  companyPhone: '+263 773 450 370',
  companyAddress: '13 Tudor Gardens, Corner Mazowe & J. Tongogara, Harare, Zimbabwe',
  companyVatNumber: 'ZW2000001',
  logoUrl: '/assets/images/1212-300x300-1786993879424.png',
  taxRate: 0.15,
  vatIncluded: true,
  currency: 'USD',
  senderEmails: ['maimba@kes.co.zw', 'info@kes.co.zw'],
  bankDetails: {
    bankName: 'CBZ Bank',
    accountName: 'Kuttingedge Solutions (Pvt) Ltd',
    accountNumber: '0123456789',
    branchCode: '001',
    swiftCode: 'COBZZWHX',
  },
};

const CompanySettingsContext = createContext<CompanySettingsContextValue>({
  settings: DEFAULT_SETTINGS,
  updateSettings: () => {},
  resetSettings: () => {},
});

function rowToSettings(row: any): CompanySettings {
  return {
    companyName: row.company_name,
    companyTagline: row.company_tagline,
    companyEmail: row.company_email,
    companyPhone: row.company_phone,
    companyAddress: row.company_address,
    companyVatNumber: row.company_vat_number,
    logoUrl: row.logo_url,
    taxRate: Number(row.tax_rate),
    vatIncluded: row.vat_included !== false,
    currency: row.currency,
    senderEmails: row.sender_emails || [],
    bankDetails: {
      bankName: row.bank_name,
      accountName: row.account_name,
      accountNumber: row.account_number,
      branchCode: row.branch_code,
      swiftCode: row.swift_code,
    },
  };
}

export function CompanySettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<CompanySettings>(DEFAULT_SETTINGS);
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const supabase = createClient();

  useEffect(() => {
    supabase
      .from('company_settings')
      .select('*')
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setSettingsId(data.id);
          setSettings(rowToSettings(data));
        }
      })
      .catch(() => {});
  }, []);

  const updateSettings = useCallback(async (partial: Partial<CompanySettings>) => {
    const merged = { ...settings, ...partial };
    setSettings(merged);

    const updateData: any = {
      company_name: merged.companyName,
      company_tagline: merged.companyTagline,
      company_email: merged.companyEmail,
      company_phone: merged.companyPhone,
      company_address: merged.companyAddress,
      company_vat_number: merged.companyVatNumber,
      logo_url: merged.logoUrl,
      tax_rate: merged.taxRate,
      vat_included: merged.vatIncluded,
      currency: merged.currency,
      sender_emails: merged.senderEmails,
      bank_name: merged.bankDetails.bankName,
      account_name: merged.bankDetails.accountName,
      account_number: merged.bankDetails.accountNumber,
      branch_code: merged.bankDetails.branchCode,
      swift_code: merged.bankDetails.swiftCode,
    };

    if (settingsId) {
      try {
        await supabase.from('company_settings').update(updateData).eq('id', settingsId);
      } catch {}
    } else {
      try {
        const { data } = await supabase.from('company_settings').insert(updateData).select().single();
        if (data) setSettingsId(data.id);
      } catch {}
    }
  }, [settings, settingsId, supabase]);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    if (settingsId) {
      supabase.from('company_settings').update({
        company_name: DEFAULT_SETTINGS.companyName,
        company_tagline: DEFAULT_SETTINGS.companyTagline,
        company_email: DEFAULT_SETTINGS.companyEmail,
        company_phone: DEFAULT_SETTINGS.companyPhone,
        company_address: DEFAULT_SETTINGS.companyAddress,
        company_vat_number: DEFAULT_SETTINGS.companyVatNumber,
        logo_url: DEFAULT_SETTINGS.logoUrl,
        tax_rate: DEFAULT_SETTINGS.taxRate,
        vat_included: DEFAULT_SETTINGS.vatIncluded,
        currency: DEFAULT_SETTINGS.currency,
        sender_emails: DEFAULT_SETTINGS.senderEmails,
        bank_name: DEFAULT_SETTINGS.bankDetails.bankName,
        account_name: DEFAULT_SETTINGS.bankDetails.accountName,
        account_number: DEFAULT_SETTINGS.bankDetails.accountNumber,
        branch_code: DEFAULT_SETTINGS.bankDetails.branchCode,
        swift_code: DEFAULT_SETTINGS.bankDetails.swiftCode,
      }).eq('id', settingsId).catch(() => {});
    }
  }, [settingsId, supabase]);

  const value = useMemo(() => ({ settings, updateSettings, resetSettings }), [settings, updateSettings, resetSettings]);

  return (
    <CompanySettingsContext.Provider value={value}>
      {children}
    </CompanySettingsContext.Provider>
  );
}

export function useCompanySettings() {
  return useContext(CompanySettingsContext);
}
